import type {
  Company,
  CompanyCreate,
  Contact,
  ContactCreate,
  ContactObject,
  ContactUpdate,
  ExternalInvite,
  ExternalInviteCreate,
  ExternalInviteResendResponse,
  ExternalInviteResponse,
  TeamInvite,
  TeamInviteCreate,
  TeamInviteResendResponse,
  TeamInviteResponse,
  Truck,
  TruckCreate,
  TruckUpdate,
  User,
  UserUpdate,
} from '../types/user'
import { api } from './fetch'

// In-flight request cache to deduplicate concurrent requests
const inFlightRequests = new Map<string, Promise<unknown>>()

export const userService = {
  async getUsers(stakeholderTypes: string[]): Promise<User[]> {
    const response = await api.get<User[]>(
      `/user/?stakeholder_type=${stakeholderTypes.join(',')}`,
    )
    return response.data
  },

  async getUsersByCompanyAndType(
    companyId: number,
    userType: string,
  ): Promise<User[]> {
    const response = await api.get<User[]>(
      `/user/?company=${companyId}&user_type=${userType}`,
    )
    return response.data
  },

  async getUserById(userId: number): Promise<User> {
    const response = await api.get<User>(`/user/${userId}`)
    return response.data
  },

  async getContactsByType(stakeholderTypes: string[]): Promise<Contact[]> {
    try {
      const contacts = await this.getContacts()
      return contacts.filter(
        (contact) =>
          (contact.contact_object.type === 'user' ||
            contact.contact_object.type === 'preuser') &&
          contact.contact_object.stakeholder_types.some((type) =>
            stakeholderTypes.includes(type),
          ),
      )
    } catch (error) {
      console.error(
        `Failed to fetch contacts with types ${stakeholderTypes}:`,
        error,
      )
      throw error
    }
  },

  async getBrokers(): Promise<User[]> {
    return this.getUsers(['BROKER'])
  },

  async getContractors(): Promise<User[]> {
    return this.getUsers(['CONTRACTOR'])
  },

  async getContacts(): Promise<Contact[]> {
    const cacheKey = 'getContacts'

    // Return existing in-flight request if one exists
    if (inFlightRequests.has(cacheKey)) {
      return inFlightRequests.get(cacheKey) as Promise<Contact[]>
    }

    // Create new request and cache it
    const requestPromise = (async () => {
      try {
        const response = await api.get<Contact[]>('/contacts/')
        return response.data
      } finally {
        inFlightRequests.delete(cacheKey)
      }
    })()

    inFlightRequests.set(cacheKey, requestPromise)
    return requestPromise
  },

  async addContact(data: ContactCreate): Promise<Contact> {
    console.log('Sending contact data:', data)
    const response = await api.post<Contact>('/contacts/', data)
    return response.data
  },

  async deleteContact(contactId: number): Promise<void> {
    await api.delete(`/contacts/${contactId}`)
  },

  async updateContact(
    contactId: number,
    data: ContactUpdate,
  ): Promise<Contact> {
    const response = await api.patch<Contact>(`/contacts/${contactId}`, data)
    return response.data
  },

  async uploadLogo(logoFile: File): Promise<User> {
    const formData = new FormData()
    formData.append('logo', logoFile)

    const response = await api.post<User>('/user/logo/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  async uploadCompanyLogo(logoFile: File): Promise<User> {
    const formData = new FormData()
    formData.append('logo', logoFile)

    const response = await api.post<User>(
      '/user/company/logo/upload/',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
    return response.data
  },

  async updateUser(userId: number, data: UserUpdate): Promise<User> {
    const response = await api.patch<User>(`/user/${userId}/`, data)
    return response.data
  },

  async updateProfileImage(userId: number, profileImage: File): Promise<User> {
    const formData = new FormData()
    formData.append('profile_image', profileImage)

    const response = await api.post<User>(
      `/user/${userId}/profile-image/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
    return response.data
  },

  async createCompany(
    userId: number,
    companyData: CompanyCreate,
  ): Promise<Company> {
    const response = await api.post<Company>(
      `/user/${userId}/company/`,
      companyData,
    )
    return response.data
  },

  async getUserCompany(): Promise<Company | null> {
    try {
      const response = await api.get<Company>('/user/company')
      return response.data
    } catch (error: unknown) {
      if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'status' in error.response &&
        error.response.status === 404
      ) {
        // User has no company associated
        return null
      }
      throw error
    }
  },

  async updateCompany(
    companyId: number,
    companyData: Partial<CompanyCreate>,
  ): Promise<Company> {
    const response = await api.patch('/user/company', companyData)
    return response.data
  },

  async getUserCompanyPublic(userId: number): Promise<{
    name: string
    address: string
    street_address?: string
    city?: string
    state?: string
    zip_code?: string
    logo_url: string | null
  } | null> {
    const cacheKey = `getUserCompanyPublic:${userId}`

    // Return existing in-flight request if one exists
    if (inFlightRequests.has(cacheKey)) {
      return inFlightRequests.get(cacheKey) as Promise<{
        name: string
        address: string
        street_address?: string
        city?: string
        state?: string
        zip_code?: string
        logo_url: string | null
      } | null>
    }

    // Create new request and cache it
    const requestPromise = (async () => {
      try {
        const response = await api.get<{
          name: string
          address: string
          street_address?: string
          city?: string
          state?: string
          zip_code?: string
          logo_url: string | null
        }>(`/user/${userId}/company/public`)
        return response.data
      } catch (error: unknown) {
        if (
          error &&
          typeof error === 'object' &&
          'response' in error &&
          error.response &&
          typeof error.response === 'object' &&
          'status' in error.response &&
          error.response.status === 404
        ) {
          // User has no company associated
          return null
        }
        throw error
      } finally {
        // Remove from cache after request completes
        inFlightRequests.delete(cacheKey)
      }
    })()

    inFlightRequests.set(cacheKey, requestPromise)
    return requestPromise
  },

  async completeDriverOnboarding(userId: number): Promise<User> {
    const response = await api.post<User>(
      `/user/${userId}/complete-driver-onboarding/`,
    )
    return response.data
  },

  async completeContractorOnboarding(userId: number): Promise<User> {
    const response = await api.post<User>(
      `/user/${userId}/complete-contractor-onboarding/`,
    )
    return response.data
  },

  async updateEid(userId: number, eid: string): Promise<User> {
    const response = await api.patch<User>(`/user/${userId}/eid/`, `"${eid}"`, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    return response.data
  },

  // Team invite functions
  async sendTeamInvite(data: TeamInviteCreate): Promise<TeamInviteResponse> {
    const response = await api.post<TeamInviteResponse>('/team/invite/', data)
    return response.data
  },

  async getTeamInvitations(): Promise<TeamInvite[]> {
    const response = await api.get<TeamInvite[]>('/team/invitations/')
    return response.data
  },

  async getTeamMembers(params?: { user_type?: string }): Promise<User[]> {
    const queryParams = new URLSearchParams()
    if (params?.user_type) queryParams.append('user_type', params.user_type)
    const queryString = queryParams.toString()
    const url = queryString ? `/team/members/?${queryString}` : '/team/members/'
    const response = await api.get<User[]>(url)
    return response.data
  },

  async deleteTeamInvitation(invitationId: number): Promise<void> {
    await api.delete(`/team/invitations/${invitationId}/`)
  },

  async resendTeamInvitation(
    invitationId: number,
  ): Promise<TeamInviteResendResponse> {
    const response = await api.post<TeamInviteResendResponse>(
      `/team/invitations/${invitationId}/resend/`,
    )
    return response.data
  },

  async getTeamInviteByPromoCode(promoCode: string): Promise<TeamInvite> {
    const response = await api.get<TeamInvite>(`/team/invite/${promoCode}/`)
    return response.data
  },

  /**
   * Check if a contact with the given email exists in the user's directory.
   * @param email - The email address to check
   * @returns Object with exists (in directory) and user_exists (registered user) flags
   */
  async checkContactExists(
    email: string,
  ): Promise<{ exists: boolean; user_exists: boolean; email: string }> {
    const response = await api.get<{
      exists: boolean
      user_exists: boolean
      email: string
    }>(`/contacts/check-exists?email=${encodeURIComponent(email)}`)
    return response.data
  },

  // External invite functions
  async sendExternalInvite(
    data: ExternalInviteCreate,
  ): Promise<ExternalInviteResponse> {
    const response = await api.post<ExternalInviteResponse>(
      '/team/external-invite/',
      data,
    )
    return response.data
  },

  async getExternalInvitations(): Promise<ExternalInvite[]> {
    const response = await api.get<ExternalInvite[]>(
      '/team/external-invitations/',
    )
    return response.data
  },

  async deleteExternalInvitation(invitationId: number): Promise<void> {
    await api.delete(`/team/external-invitations/${invitationId}/`)
  },

  async resendExternalInvitation(
    invitationId: number,
  ): Promise<ExternalInviteResendResponse> {
    const response = await api.post<ExternalInviteResendResponse>(
      `/team/external-invitations/${invitationId}/resend/`,
    )
    return response.data
  },
}
